"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import styles from "../export.module.css";

// TradingView Mini Symbol Overview Widget
const TradingViewWidget: React.FC<{
  symbol: string;
  locale: string;
  chartHeight?: number;
}> = ({ symbol, locale, chartHeight = 260 }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.innerHTML = "";

      const widgetContainer = document.createElement("div");
      widgetContainer.className = "tradingview-widget-container";
      widgetContainer.style.width = "100%";
      widgetContainer.style.height = `${chartHeight}px`;

      const widget = document.createElement("div");
      widget.className = "tradingview-widget-container__widget";
      widget.style.width = "100%";
      widget.style.height = "100%";

      const script = document.createElement("script");
      script.src =
        "https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js";
      script.type = "text/javascript";
      script.async = true;
      script.innerHTML = JSON.stringify({
        symbol,
        width: "100%",
        height: chartHeight,
        locale: locale === "vi" ? "vi" : "en",
        dateRange: "1D",
        colorTheme: "light",
        isTransparent: true,
        autosize: false,
        largeChartUrl: "",
        noTimeScale: false,
      });

      widgetContainer.appendChild(widget);
      widgetContainer.appendChild(script);
      containerRef.current.appendChild(widgetContainer);
    }, 800);

    return () => clearTimeout(timer);
  }, [symbol, locale, chartHeight]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: `${chartHeight}px` }}
    />
  );
};

export interface PriceSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    description?: string;
  };
  locale: string;
}

export default function PriceSection({ data, locale }: PriceSectionProps) {
  if (!data || !data.title) return null;

  return (
    <section className={styles.priceSection}>
      {/* Background Decoration */}
      <div className={styles.priceDecoWrapper} aria-hidden="true">
        <Image
          src="/images/tea_deco.png"
          alt=""
          width={240}
          height={240}
          className={styles.priceDecoBottomLeft}
          unoptimized
        />
      </div>

      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
          {data.description && (
            <Description
              as="p"
              size="medium"
              align="center"
              className={styles.sectionDesc}
            >
              {data.description}
            </Description>
          )}
        </div>

        <div className={styles.priceGrid}>
          {/* London Robusta */}
          <div className={styles.priceCard}>
            <div
              className={`${styles.priceCardHeader} ${styles.priceCardHeaderRobusta}`}
            >
              <Title
                as="h3"
                size="small"
                color="#ffffff"
                align="center"
                className={styles.priceHeaderTitleCentered}
              >
                {locale === "vi"
                  ? "ROBUSTA - SÀN LONDON (ICE FUTURES EU)"
                  : "ROBUSTA - LONDON EXCHANGE (ICE FUTURES EU)"}
              </Title>
            </div>

            <div className={styles.priceCardBody} style={{ padding: "0" }}>
              <TradingViewWidget
                symbol="CMCMARKETS:ROBCOFFEE"
                locale={locale}
                chartHeight={260}
              />
            </div>
          </div>

          {/* New York Arabica */}
          <div className={styles.priceCard}>
            <div
              className={`${styles.priceCardHeader} ${styles.priceCardHeaderArabica}`}
            >
              <Title
                as="h3"
                size="small"
                color="#ffffff"
                align="center"
                className={styles.priceHeaderTitleCentered}
              >
                {locale === "vi"
                  ? "ARABICA - SÀN NEW YORK (ICE FUTURES US)"
                  : "ARABICA - NEW YORK EXCHANGE (ICE FUTURES US)"}
              </Title>
            </div>

            <div className={styles.priceCardBody} style={{ padding: "0" }}>
              <TradingViewWidget
                symbol="CMCMARKETS:USCOFFEE"
                locale={locale}
                chartHeight={260}
              />
            </div>
          </div>
        </div>

        <div className={styles.priceSource}>
          {locale === "vi" ? "Nguồn: tradingview.com" : "Source: Barchart.com"}
        </div>
      </div>
    </section>
  );
}
